import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, Navigate, Link, useLocation } from "react-router-dom";
import { usePartnerAuth } from "../../auth/PartnerAuthProvider";
import { useAuth } from "../../auth/useAuth";
import { usePartnerPortalBase } from "./PartnerPortalBaseProvider";
import { CASAMAPA_CLIENT_VIEW_SLUG } from "../../constants/casamapaPartnerPortalConfig";
import { useOptionalLocale } from "../../intl";
import { filterCasaMapaPartnerTabs } from "../../utils/casamapaPartnerPortalTabs";
import PartnerPortalLocaleControls from "./PartnerPortalLocaleControls";
import "../../styles/partnerPortalDashboard.css";

function partnerInitials(partner) {
  const source = partner?.displayName || partner?.businessName || partner?.email || "P";
  const parts = String(source).trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return String(source).slice(0, 2).toUpperCase();
}

function portalMark(label) {
  const words = String(label || "Partner").split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  return String(label || "P").slice(0, 2).toUpperCase();
}

function PartnerPortalNavLink({ tab, to, end, label }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `partner-portal-nav__link${isActive ? " is-active" : ""}`
      }
    >
      <span className="partner-portal-nav__icon" aria-hidden="true">
        {tab.icon}
      </span>
      <span>{label}</span>
    </NavLink>
  );
}

const PartnerPortalLayoutContent = () => {
  const { partner, loading, logout, isAuthenticated } = usePartnerAuth();
  const { user, isSuperAdmin, loading: platformAuthLoading } = useAuth();
  const { clientViewSlug, loginPath, portalLabel, portalLoadingLabel, portalNavAriaLabel, tabs, path } =
    usePartnerPortalBase();
  const localeCtx = useOptionalLocale();
  const t = localeCtx?.t || ((key) => key);
  const location = useLocation();
  const isCasaMapaPortal = clientViewSlug === CASAMAPA_CLIENT_VIEW_SLUG;
  const isPlatformOperator = Boolean(isSuperAdmin);
  const canAccessPortal = isPlatformOperator || isAuthenticated;
  const [menuOpen, setMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const menuRef = useRef(null);
  const moreRef = useRef(null);

  const { primaryTabs, secondaryTabs } = useMemo(() => {
    let visibleTabs = isCasaMapaPortal ? filterCasaMapaPartnerTabs(tabs, partner) : tabs;
    if (partner?.features?.whatsappMonitoring === false) {
      visibleTabs = visibleTabs.filter((tab) => tab.segment !== "whatsapp");
    }
    const primary = visibleTabs.filter((tab) => tab.navGroup !== "secondary");
    const secondary = visibleTabs.filter((tab) => tab.navGroup === "secondary");
    return { primaryTabs: primary, secondaryTabs: secondary };
  }, [isCasaMapaPortal, tabs, partner]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
      if (moreRef.current && !moreRef.current.contains(event.target)) {
        setMoreOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  if (loading || (!isAuthenticated && platformAuthLoading)) {
    return (
      <div className="partner-portal-app">
        <p className="partner-portal-loading muted">{portalLoadingLabel}</p>
      </div>
    );
  }

  if (!canAccessPortal) {
    const loginTarget = `${loginPath}?returnTo=${encodeURIComponent(location.pathname + location.search)}`;
    return <Navigate to={loginTarget} replace />;
  }

  const displayName = isPlatformOperator
    ? user?.displayName || partner?.displayName || "System Admin"
    : partner?.displayName || partner?.businessName || user?.displayName || "Partner";
  const accountEmail = isPlatformOperator
    ? user?.email || partner?.email || ""
    : partner?.email || user?.email || "";
  const profilePartner = partner || {
    displayName,
    businessName: displayName,
    email: accountEmail,
  };

  const tabLabel = (tab) => (tab.labelKey ? t(tab.labelKey) : tab.label);

  return (
    <div className="partner-portal-app">
      <header className="partner-portal-topbar">
        <div className="partner-portal-topbar__inner">
          <Link className="partner-portal-brand" to={path("dashboard")}>
            <span className="partner-portal-brand__mark" aria-hidden="true">
              {portalMark(portalLabel)}
            </span>
            <span className="partner-portal-brand__copy">
              <strong>{portalLabel}</strong>
              <small>{displayName}</small>
            </span>
          </Link>

          <nav className="partner-portal-nav" aria-label={portalNavAriaLabel}>
            {primaryTabs.map((tab) => (
              <PartnerPortalNavLink
                key={tab.segment}
                tab={tab}
                label={tabLabel(tab)}
                to={path(tab.segment)}
                end={tab.segment !== "dealers"}
              />
            ))}

            {secondaryTabs.length ? (
              <div className="partner-portal-nav__more" ref={moreRef}>
                <button
                  type="button"
                  className={`partner-portal-nav__more-trigger${moreOpen ? " is-open" : ""}`}
                  aria-expanded={moreOpen}
                  aria-haspopup="true"
                  onClick={() => setMoreOpen((open) => !open)}
                >
                  More
                  <span aria-hidden="true">▾</span>
                </button>
                {moreOpen ? (
                  <div className="partner-portal-nav__dropdown">
                    {secondaryTabs.map((tab) => (
                      <NavLink
                        key={tab.segment}
                        to={path(tab.segment)}
                        className={({ isActive }) =>
                          `partner-portal-nav__dropdown-link${isActive ? " is-active" : ""}`
                        }
                        onClick={() => setMoreOpen(false)}
                      >
                        <span className="partner-portal-nav__icon" aria-hidden="true">
                          {tab.icon}
                        </span>
                        {tabLabel(tab)}
                      </NavLink>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </nav>

          <div className="partner-portal-user" ref={menuRef}>
            <button
              type="button"
              className="partner-portal-user__trigger"
              aria-expanded={menuOpen}
              aria-haspopup="true"
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className="partner-portal-user__avatar">{partnerInitials(profilePartner)}</span>
              <span className="partner-portal-user__meta">
                <strong>{displayName}</strong>
                <small>{accountEmail}</small>
              </span>
              <span className="partner-portal-user__chevron" aria-hidden="true">
                ▾
              </span>
            </button>

            {menuOpen ? (
              <div className="partner-portal-user__menu">
                <div className="partner-portal-user__menu-head">
                  <strong>{displayName}</strong>
                  <span>{accountEmail}</span>
                  {partner?.accountStatus ? (
                    <span className="partner-portal-user__status">{partner.accountStatus}</span>
                  ) : isPlatformOperator ? (
                    <span className="partner-portal-user__status">Platform admin</span>
                  ) : null}
                </div>
                <div
                  className="partner-portal-user__menu-locale"
                  onClick={(event) => event.stopPropagation()}
                >
                  <PartnerPortalLocaleControls variant="menu" />
                </div>
                {clientViewSlug === CASAMAPA_CLIENT_VIEW_SLUG ? (
                  <>
                    <Link
                      className="partner-portal-user__menu-link"
                      to={path("profile")}
                      onClick={() => setMenuOpen(false)}
                    >
                      {t("realEstate.casaMapa.admin.partnerSettings.menu.account")}
                    </Link>
                    <Link
                      className="partner-portal-user__menu-link"
                      to={path("profile/inventory")}
                      onClick={() => setMenuOpen(false)}
                    >
                      {t("realEstate.casaMapa.admin.partnerSettings.menu.inventory")}
                    </Link>
                    <Link
                      className="partner-portal-user__menu-link"
                      to={path("profile/crm")}
                      onClick={() => setMenuOpen(false)}
                    >
                      {t("realEstate.casaMapa.admin.partnerSettings.menu.crm")}
                    </Link>
                  </>
                ) : (
                  <Link
                    className="partner-portal-user__menu-link"
                    to={path("profile")}
                    onClick={() => setMenuOpen(false)}
                  >
                    Account profile
                  </Link>
                )}
                <button
                  type="button"
                  className="partner-portal-user__signout"
                  onClick={() => {
                    setMenuOpen(false);
                    logout();
                  }}
                >
                  Sign out
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <main className="partner-portal-main">
        <Outlet />
      </main>
    </div>
  );
};

const PartnerPortalLayout = () => <PartnerPortalLayoutContent />;

export default PartnerPortalLayout;
